import {
  ensure,
  ensureProperties,
  wrapErrors,
  asArrayOfStrings,
  notEmpty,
  itsProperty,
} from "../Common/composition";

import {
  bitMaskAsDays,
  daysAsBitMask,
  renderRangeDays,
  RangeDays,
  RangeDaysInput,
  WEEKDAYS,
  Day,
} from "./RangeDays";
import TimeInput from "./Time";

const validateRangeDays = wrapErrors(
  (error) => `selected days ${error}`,
  ensure(asArrayOfStrings(notEmpty), daysAsBitMask)
);
const invalid = (error) => {
  throw error;
};

export const toMinutes = (input) =>
  input
    .split(":")
    .reduce(
      (total, part, idx) => total + parseInt(part, 10) * (idx === 0 ? 60 : 1),
      0
    );

const validateRangeDuration = 
  ({ start, end, ...rest }) =>
    toMinutes(start) >= toMinutes(end)
      ? invalid(`start must precede the end time`)
      : { start, end, ...rest }

const asIts = (result) => result;

const matchsOnAnyDay = (days, otherDays) =>
  days.find((day) => otherDays.includes(day)) !== undefined;

// Two spans that just touch at a boundary, such as 8:00-13:00 and 13:00-24:00,
// are adjacent and not overlapping, so the equality is not included.
const overlapsTimeSpans = ([start, end], [otherStart, otherEnd]) =>
  Math.max(start, otherStart) - Math.min(end, otherEnd) < 0;

const anotherRangeMatchingDaysOrSpanOf = (reference) => {
  const [start, end, days] = [
    toMinutes(reference.start),
    toMinutes(reference.end),
    asArrayOfStrings(asIts)(reference.days),
  ];
  return (other) =>
    other.deleted !== true &&
    other !== reference &&
    other.__id !== reference.__id &&
    matchsOnAnyDay(days, asArrayOfStrings(asIts)(other.days)) &&
    overlapsTimeSpans(
      [start, end],
      [toMinutes(other.start), toMinutes(other.end)]
    );
};

const addQuotesAndCommas = (day, index, all) =>
  all.length === 1 || index === 0
    ? `"${day}"`
    : index + 1 === all.length
    ? `and "${day}"`
    : `, "${day}"`;

const quotedAndCommaSeparated = (result) =>
  result.map(addQuotesAndCommas).join("");

const enumerateDays = (input) =>
  asArrayOfStrings(quotedAndCommaSeparated)(input);

const describeRange = ({ start, end, days }) =>
  `"${start}" - "${end}" time range on ${enumerateDays(days)}`;

const wontAllowOverlapping = (target, overlapping) =>
  overlapping === undefined
    ? target
    : invalid(
        `Can't add range ${describeRange(target)} as overlaps ${describeRange(
          overlapping
        )}`
      );

const validateNotOverlaping = (item, list) =>
  wontAllowOverlapping(item, list.find(anotherRangeMatchingDaysOrSpanOf(item)));

const verifyValiedRange = wrapErrors(
  (error) => `Time range ${error}`,
  ensureProperties(
    itsProperty("days", validateRangeDays),
    validateRangeDuration,
    validateNotOverlaping
  )
);

const removesPadding = (input) => input.replace(/^0/, "");

// Hours 00-23 and minutes 00-59, with an optional leading zero in the hours.
const validTimeOfDay = /^(0?[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;

// "24:00" is midnight and is only accepted as the end of a range.
const isAValid24HourFormat = (allowEndOfDay) => (input) =>
  validTimeOfDay.test(input) || (allowEndOfDay === true && input === "24:00")
    ? input
    : invalid("is not a valid 24h hours and minutes");

const validateHoursAndMinutesOf = (term, allowEndOfDay = false) =>
  wrapErrors(
    (error) => `${term} time ${error}`,
    ensure(isAValid24HourFormat(allowEndOfDay), removesPadding)
  );

export const validateStartTime = validateHoursAndMinutesOf("Start");
export const validateEndTime = validateHoursAndMinutesOf("End", /*allowEndOfDay*/ true);

const timeComposition = {
  cliType: "time",
  typeLabel: "Time",
  viewURL: "viewTimeProfileConfig",
  expectClear: true,
  redeclareToUpdate: true,
  profileFn: (row)=>{
    return {
      stored: true, 
      pristine: true,
      name: row["NAME"],
      ranges: row["START"].map(item=> {
        return {
          start: item["START"],  
        };
      }),
    }
  },
  fields: [
    { name: "name", label: "Profile name", type: "text", canUpdate: true, resName: "NAME", },
    {
      name: "ranges",
      cliName: "range",
      label: "Time ranges",
      labelSingle: "Time range",
      type: "many",
      labelColumn: "START",
      validate: verifyValiedRange,
      canBeEmpty: false,
      resName: "START",
      fields: [
        {
          name: "start",
          label: "Start",
          type: "text",
          icon: "access_time",
          InputComponent: TimeInput,
          editionClassName: "one-column",
          defaultValue: "00:00",
          validate: validateStartTime,
          resName: "START",
        },
        {
          name: "end",
          label: "End",
          type: "text",
          icon: "access_time",
          InputComponent: TimeInput,
          editionClassName: "one-column",
          defaultValue: "24:00",
          validate: validateEndTime,
          resName: "END",
        },
        {
          name: "days",
          label: "Days",
          type: "days",
          icon: "today",
          parse: bitMaskAsDays,
          renderValue: renderRangeDays,
          renderCell: (value, type)=> {
            const checkedDaysInt = bitMaskAsDays(value);
            const checkedDays = value.split(', ');
            return <div
            class="btn-toolbar nowrap align-center0"
            role="toolbar"
            aria-label="Range active days"
          >{WEEKDAYS.map((day, index)=>{
              const checked = type === "edit" ? checkedDays.includes(day) : checkedDaysInt.includes(day);
              return <Day key={`${day}`} day={day} checked={checked}/>
            })}</div>;
          },
          Component: RangeDays,
          InputComponent: RangeDaysInput,
          validate: validateRangeDays,
          resName: "DAYS",
        },
      ],
    },
  ],
};

export default timeComposition;
